import { request } from '@umijs/max';
import { collectObjectCandidates } from '@/utils/response';

export type UserProfile = {
  id: string;
  username: string;
  email: string;
  phone?: string | null;
  nickname?: string | null;
  avatarUrl?: string | null;
  status: 'ACTIVE' | 'DISABLED' | 'PENDING';
  roles: string[];
  memberLevel: 'FREE' | 'BASIC' | 'PRO' | 'MAX';
  memberExpireAt?: string | null;
  membershipQuota?: {
    memberLevel?: 'FREE' | 'BASIC' | 'PRO' | 'MAX' | null;
    aiRobotQuota?: number | null;
    keywordTemplateQuota?: number | null;
    keywordTemplateItemQuota?: number | null;
    qaTemplateQuota?: number | null;
    qaTemplateItemQuota?: number | null;
  } | null;
  dedicatedServer?: {
    publicIp?: string | null;
  } | null;
  aggregationServerIp?: string | null;
  aggregationServerReady?: boolean;
  createdAt: string;
};

export type LoginRequest = {
  usernameOrEmail: string;
  password: string;
};

export type PhoneCodeLoginRequest = {
  phone: string;
  smsCode: string;
};

export type LoginResponse = {
  accessToken: string;
  tokenType: string;
  expiresInSeconds: number;
  user: UserProfile;
};

export type SendSmsCodeResponse = {
  success: boolean;
  phone: string;
  scene: string;
  sendIntervalSeconds: number;
  expireSeconds: number;
};

export type ChangePhoneRequest = {
  phone: string;
  smsCode: string;
};

export type ResetPasswordByPhoneRequest = {
  phone: string;
  smsCode: string;
  newPassword: string;
};

export type ActionMessageResponse = {
  success: boolean;
  message: string;
};

const isUserProfileShape = (value: Record<string, any>) => {
  const hasId = value.id !== undefined || value.userId !== undefined;
  const hasIdentity =
    value.username !== undefined ||
    value.email !== undefined ||
    Array.isArray(value.roles);
  return hasId && hasIdentity;
};

const unwrapUserProfilePayload = (payload: any): UserProfile => {
  const candidates = collectObjectCandidates(payload, [
    'data',
    'result',
    'payload',
    'content',
    'user',
  ]);
  const profileCandidate = candidates.find(isUserProfileShape);
  if (!profileCandidate) {
    throw new Error('用户信息返回格式异常');
  }

  return {
    ...(profileCandidate as UserProfile),
    id: String(profileCandidate.id ?? profileCandidate.userId ?? ''),
    roles: Array.isArray(profileCandidate.roles)
      ? profileCandidate.roles.map(String)
      : [],
  };
};

export const loginByUserService = (body: LoginRequest) => {
  return request<LoginResponse>('/bff/gateway/user/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    skipErrorHandler: true,
  });
};

export const loginByPhoneCodeService = (body: PhoneCodeLoginRequest) => {
  return request<LoginResponse>('/bff/gateway/user/login/phone-code', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    skipErrorHandler: true,
  });
};

export const sendLoginSmsCodeByUserService = (phone: string) => {
  return request<SendSmsCodeResponse>('/bff/gateway/user/sms-code/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: { phone },
    skipErrorHandler: true,
  });
};

export const sendResetPasswordSmsCodeByUserService = (phone: string) => {
  return request<SendSmsCodeResponse>(
    '/bff/gateway/user/sms-code/reset-password',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data: { phone },
      skipErrorHandler: true,
    },
  );
};

export const resetPasswordByPhoneByUserService = (
  body: ResetPasswordByPhoneRequest,
) => {
  return request<ActionMessageResponse>(
    '/bff/gateway/user/password/reset-by-phone',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data: body,
      skipErrorHandler: true,
    },
  );
};

export const queryCurrentUserProfile = (options?: { [key: string]: any }) => {
  return request<UserProfile>('/bff/gateway/user/profile', {
    method: 'GET',
    ...(options || {}),
  }).then(unwrapUserProfilePayload);
};

export const sendChangePhoneSmsCodeByUserService = (phone: string) => {
  return request<SendSmsCodeResponse>('/bff/gateway/user/profile/phone/sms-code', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: { phone },
  });
};

export const updatePhoneByUserService = (body: ChangePhoneRequest) => {
  return request<UserProfile>('/bff/gateway/user/profile/phone', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
  }).then(unwrapUserProfilePayload);
};

export const logoutByUserService = () => {
  return request('/bff/gateway/user/logout', {
    method: 'POST',
  });
};

export const queryMembership = async (options?: { [key: string]: any }) => {
  // membership 与 profile 返回结构一致；统一走 BFF profile，避免在浏览器暴露专属服务器地址。
  return queryCurrentUserProfile(options);
};

export const toCurrentUser = (profile: UserProfile): API.CurrentUser => {
  const isAdmin = profile.roles.includes('ROLE_ADMIN');
  const aggregationHttpBaseUrl = '/bff/execute';

  return {
    userid: String(profile.id),
    name: profile.nickname || profile.username,
    avatar: profile.avatarUrl || undefined,
    email: profile.email,
    phone: profile.phone || undefined,
    title: profile.memberLevel,
    group: profile.status,
    access: isAdmin ? 'admin' : 'user',
    aggregationHttpBaseUrl,
    aggregationServerReady:
      typeof profile.aggregationServerReady === 'boolean'
        ? profile.aggregationServerReady
        : true,
    memberLevel: profile.memberLevel,
    memberExpireAt: profile.memberExpireAt ?? undefined,
  };
};
